// components/modals/CreateJobModal.js
import React, { useState } from "react";
import { jobsAPI, masterFilesAPI } from "../../../services/api";
import { validateFiles, formatFileSize } from "../../utils/jobUtils";
import {
  readWB,
  parseTax,
  parseAR04,
  mergeMaster,
  compareAR04WithMaster,
} from "../../excelUtils";

const CreateJobModal = ({ show, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  // Form state
  const [newJob, setNewJob] = useState({
    job_name: "",
    description: "",
    due_date: "",
  });

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState({
    tax: null,
    ar04: null,
  });

  if (!show) return null;

  const handleFileSelect = (fileType, file) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [fileType]: file,
    }));
  };

  const createJobWithFiles = async () => {
    if (!newJob.job_name.trim()) {
      alert("กรุณาใส่ชื่องาน");
      return;
    }

    const validation = validateFiles(selectedFiles);
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // Create job first
      console.log("📝 Creating new job...");
      const jobResponse = await jobsAPI.create(newJob);
      const jobId = jobResponse.data.data.id;

      console.log(`✅ Job created with ID: ${jobId}`);
      setUploadProgress(10);

      // Upload both files to the job
      console.log("📤 Uploading both files to job...");
      const formData = new FormData();
      formData.append("tax_file", selectedFiles.tax);
      formData.append("ar04_file", selectedFiles.ar04);

      await jobsAPI.uploadFiles(jobId, formData);
      setUploadProgress(30);

      // Process Tax.xlsx file locally
      console.log("🔄 Processing Tax.xlsx file locally...");
      const wbTax = await readWB(selectedFiles.tax);
      const taxData = parseTax(wbTax);
      console.log(`📊 Parsed ${taxData.length} Tax records`);
      setUploadProgress(40);

      // Process AR04.xlsx file locally
      console.log("🔄 Processing AR04.xlsx file locally...");
      const wbAR04 = await readWB(selectedFiles.ar04);
      const ar04Data = parseAR04(wbAR04);
      console.log(`📊 Parsed ${ar04Data.length} AR04 records`);
      setUploadProgress(50);

      // Get existing master files from database
      console.log("📊 Loading existing master files from database...");
      const masterFilesResponse = await masterFilesAPI.getMasterData({
        limit: 10000,
      });
      let data3110 = [],
        data3119 = [];

      if (
        masterFilesResponse.data.success &&
        masterFilesResponse.data.data.length > 0
      ) {
        const dbData = masterFilesResponse.data.data;
        data3110 = dbData.map((item) => ({
          "Outlet Code": item.outlet_code,
          Site: item.site,
          "Outlet Name 3110": item.outlet_name,
          "Outlet Type Description": item.outlet_type_description,
          "Outlet SubType Description": item.outlet_subtype_description,
        }));
        data3119 = dbData.map((item) => ({
          "Outlet Code": item.outlet_code,
          "Site Name": item.site_name,
          "Outlet Name 3119": item.outlet_name,
          Status: item.status,
          "Opening Date": item.opening_date,
          "Closing Date": item.closing_date,
        }));
      }
      setUploadProgress(60);

      // Merge data using the algorithm
      console.log("🔄 Merging Tax data with Master Files...");
      const { data } = mergeMaster(taxData, data3110, data3119);
      console.log(`✅ Created merged master data with ${data.length} records`);

      // Store the merged data for this job in database
      console.log(
        `💾 Storing merged master data in database for job ${jobId}...`
      );
      try {
        await jobsAPI.storeMasterData(jobId, data);
        console.log(
          `✅ Successfully stored ${data.length} master data records`
        );
      } catch (storeError) {
        console.error("Failed to store master data:", storeError);
        alert(
          "⚠️ ไม่สามารถบันทึกข้อมูล MASTER FILE ได้ แต่ข้อมูลยังคงแสดงในหน้านี้"
        );
      }
      setUploadProgress(70);

      // Run comparison
      console.log(
        "⚖️ Running comparison between AR04 and merged master data..."
      );
      try {
        if (ar04Data.length === 0) {
          throw new Error("No AR04 data found");
        }
        if (data.length === 0) {
          throw new Error("No master data available for comparison");
        }

        const comparisonResult = compareAR04WithMaster(ar04Data, data);
        console.log(
          `✅ Generated ${comparisonResult.data.length} comparison results`
        );

        if (!comparisonResult.data || comparisonResult.data.length === 0) {
          throw new Error("Comparison generated no results");
        }

        // Store comparison results
        await jobsAPI.storeComparisonResults(jobId, comparisonResult.data);
        console.log(`✅ Successfully stored comparison results`);

        // Store initial AR04 for re-upload comparison later
        try {
          await jobsAPI.storeOriginalAR04(jobId, ar04Data);
          console.log(`✅ Stored original AR04 for future re-compare`);
        } catch (ar04StoreError) {
          console.warn(
            "Failed to store original AR04:",
            ar04StoreError.message
          );
        }

        setUploadProgress(85);
      } catch (compareError) {
        console.error("Comparison processing failed:", compareError);
        alert(`⚠️ การเปรียบเทียบไม่สำเร็จ: ${compareError.message}`);
      }

      // Update job status to completed
      console.log("✅ Updating job status to completed...");
      await jobsAPI.update(jobId, {
        status: "COMPLETED",
        progress_percentage: 100,
      });
      setUploadProgress(100);

      // Success callback
      setTimeout(() => {
        setUploadProgress(null);
        onSuccess();

        alert(
          `สร้างงาน "${newJob.job_name}" สำเร็จ!\n\n` +
            `✅ MASTER FILE พร้อมดู (${data.length} รายการ)\n` +
            `📤 ไฟล์ถูกอัปโหลดเรียบร้อย`
        );
      }, 1000);
    } catch (error) {
      console.error("Job creation failed:", error);
      setLoading(false);
      setUploadProgress(null);

      // Enhanced error handling
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || error.message;
        alert("ข้อผิดพลาด: " + errorMessage);
      } else if (error.response?.status === 413) {
        alert("ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 50MB ต่อไฟล์)");
      } else if (error.response?.status === 500) {
        alert("เกิดข้อผิดพลาดกับเซิร์ฟเวอร์ กรุณาลองใหม่ภายหลัง");
      } else if (!error.response) {
        alert("ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้");
      } else {
        alert(
          "เกิดข้อผิดพลาดในการสร้างงาน: " +
            (error.response?.data?.message || error.message)
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: "600px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 style={{ fontSize: "20px", fontWeight: "600" }}>สร้างงานใหม่</h2>
        </div>

        <div className="modal-body">
          {/* Job Details */}
          <JobDetailsForm newJob={newJob} setNewJob={setNewJob} />

          {/* File Uploads */}
          <FileUploadSection
            selectedFiles={selectedFiles}
            onFileSelect={handleFileSelect}
          />

          {/* Upload Progress */}
          {uploadProgress !== null && (
            <UploadProgress progress={uploadProgress} />
          )}

          <InfoAlert />
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={loading}
          >
            ยกเลิก
          </button>
          <button
            className="btn btn-primary"
            onClick={createJobWithFiles}
            disabled={
              loading ||
              !newJob.job_name.trim() ||
              !selectedFiles.tax ||
              !selectedFiles.ar04
            }
          >
            {loading ? "กำลังสร้างงาน..." : "สร้างงานและประมวลผล"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const JobDetailsForm = ({ newJob, setNewJob }) => (
  <>
    <div className="form-group">
      <label className="form-label">ชื่องาน *</label>
      <input
        type="text"
        className="form-input"
        value={newJob.job_name}
        onChange={(e) =>
          setNewJob((prev) => ({ ...prev, job_name: e.target.value }))
        }
        placeholder="เช่น: เปรียบเทียบ AR04 เดือนธันวาคม 2024"
      />
    </div>

    <div className="form-group">
      <label className="form-label">รายละเอียด</label>
      <textarea
        className="form-input"
        value={newJob.description}
        onChange={(e) =>
          setNewJob((prev) => ({ ...prev, description: e.target.value }))
        }
        placeholder="รายละเอียดเพิ่มเติมของงาน"
        rows="3"
      />
    </div>

    <div className="form-group">
      <label className="form-label">กำหนดเสร็จ</label>
      <input
        type="date"
        className="form-input"
        value={newJob.due_date}
        onChange={(e) =>
          setNewJob((prev) => ({ ...prev, due_date: e.target.value }))
        }
      />
    </div>
  </>
);

const FileUploadSection = ({ selectedFiles, onFileSelect }) => (
  <div className="grid grid-cols-2" style={{ gap: "16px", marginTop: "24px" }}>
    <FileUploadBox
      type="tax"
      file={selectedFiles.tax}
      onFileSelect={onFileSelect}
      title="Tax.xlsx"
      description="ข้อมูลภาษีสำหรับงานนี้"
    />
    <FileUploadBox
      type="ar04"
      file={selectedFiles.ar04}
      onFileSelect={onFileSelect}
      title="AR04.xlsx"
      description="ไฟล์ AR04 สำหรับเปรียบเทียบ"
    />
  </div>
);

const FileUploadBox = ({ type, file, onFileSelect, title, description }) => (
  <div>
    <label className="form-label">{title} *</label>
    <div
      className="upload-area"
      style={{ padding: "20px", minHeight: "120px" }}
    >
      {file ? (
        <div>
          <div className="upload-icon">✅</div>
          <div className="upload-text">{file.name}</div>
          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "8px" }}>
            {formatFileSize(file.size)}
          </div>
        </div>
      ) : (
        <div>
          <div className="upload-icon">📄</div>
          <div className="upload-text">{title}</div>
          <div className="upload-subtext">{description}</div>
          <input
            type="file"
            accept=".xls,.xlsx"
            onChange={(e) => onFileSelect(type, e.target.files[0])}
            style={{ display: "none" }}
            id={`${type}-upload`}
          />
          <label
            htmlFor={`${type}-upload`}
            className="btn btn-outline btn-sm"
            style={{ marginTop: "8px" }}
          >
            เลือกไฟล์
          </label>
        </div>
      )}
    </div>
  </div>
);

const UploadProgress = ({ progress }) => (
  <div style={{ marginTop: "24px" }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "8px",
      }}
    >
      <span>กำลังประมวลผลงาน...</span>
      <span>{progress}%</span>
    </div>
    <div
      style={{
        width: "100%",
        height: "8px",
        background: "#e2e8f0",
        borderRadius: "4px",
      }}
    >
      <div
        style={{
          width: `${progress}%`,
          height: "100%",
          background: "#6366f1",
          borderRadius: "4px",
          transition: "width 0.3s ease",
        }}
      />
    </div>
    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "8px" }}>
      {progress < 25 && "สร้างงานในระบบ..."}
      {progress >= 25 && progress < 50 && "อัปโหลดไฟล์..."}
      {progress >= 50 && progress < 75 && "รวมข้อมูลกับ Master Files..."}
      {progress >= 75 && progress < 100 && "เปรียบเทียบข้อมูล..."}
      {progress === 100 && "เสร็จสิ้น!"}
    </div>
  </div>
);

const InfoAlert = () => (
  <div className="alert alert-info" style={{ marginTop: "20px" }}>
    <span>ℹ️</span>
    <div>
      <strong>หมายเหตุ:</strong>
      <ul style={{ margin: "8px 0 0 20px", fontSize: "14px" }}>
        <li>
          <strong>ไฟล์ Tax.xlsx</strong> จะถูกรวมกับข้อมูล Master Files
        </li>
        <li>
          <strong>ใช้อัลกอริทึมเดียวกับ index.html</strong> สำหรับการรวมข้อมูล
        </li>
        <li>
          <strong>MASTER FILE จะมี 16 คอลัมน์</strong> ตามที่กำหนด
        </li>
        <li>ไฟล์ AR04.xlsx จะถูกอัปโหลดและเตรียมไว้สำหรับการเปรียบเทียบ</li>
        <li>ขนาดไฟล์สูงสุด 50MB ต่อไฟล์</li>
      </ul>
    </div>
  </div>
);

export default CreateJobModal;
